import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { ChildComponent } from "../../../child-component";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { DeficiencySeverityChange, PscActivitiesColumns, PscClientModel, PscDeficiencyClientModel, PscDeficiencyStatusClientModel, PscSeverityEnum } from "../../../../../../models/pscs/psc.models";
import { I18N } from "aurelia-i18n";
import { PscInspectionStatusEnum, SubtaskStatusEnum } from "../../../../../../models/vetting/vetting.models";
import * as moment from "moment";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Reflection } from "../../../../../../utilities/reflection";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { PscService } from "../../../../../../services/psc/psc.service";
import * as Enumerable from "linq";
import { RightStoreService } from "../../../../../../services/rightstore/rightstore.service";
import { ProductCodes } from "../../../../../../models/rightstore/rightstore.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { MyVesselService } from "../../../../../../services/my-vessel/my-vessel.service";
import { GetTasksRequest, TaskModel, TaskPriorityEnum } from "@rightship-tech/tasks-ui/models/tasks.models";
import { MyTasksService } from "../../../../../../services/my-vessel/my-tasks.service";
import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import { BindingSignaler } from "aurelia-templating-resources";

@autoinject
@transient()
@useView('views/pages/modules/psc/steps/overview/psc-overview.html')
export class PscOverview extends ChildComponent<PscClientModel> {

    reassignment: PscInspectionStatusEnum = PscInspectionStatusEnum.PscInspectionReassignment;
    rightShipUser: boolean;
    canViewPSCEditsPermission: boolean;
    canEditPSCDetailsPermission: boolean;
    canSubmitCloseout: boolean;
    canViewDataSources: boolean;
    canAccessRightShipInspectionsInActivity: boolean;
    forceLoad:boolean;
    loadVesselheader: boolean = false;
    customerId: number;
    severityChanges: DeficiencySeverityChange[] = [];
    
    public enabledNewUI: boolean;
    public deficiencyCategories;
    public selectedCategory = null;
	public vessels: { id: number, imo: string, name: string }[];
    
	public pendingTasks: TaskModel[];
    

    constructor(
        notificationsService: NotificationClientService,
        i18N: I18N,
        private router: Router,
        private dialogClientService: DialogClientService,
        private userClaimService: UserClaimsService,
        private pscService:PscService,
        private rightStoreService:RightStoreService,
        private configService: ConfigService,
        private myVesselService: MyVesselService,
        private myTaskService: MyTasksService,
        private eventAggregator: EventAggregator,
        private bindingSignaler: BindingSignaler
    ) { 
        super(notificationsService, i18N);

        this.rightShipUser = userClaimService.hasPermission(Claims.RightShipUser);
        this.canViewPSCEditsPermission = userClaimService.hasPermission(Claims.CanViewPSCEdits);
        this.canEditPSCDetailsPermission = userClaimService.hasPermission(Claims.CanEditPSCDetails);
        this.canSubmitCloseout = userClaimService.hasPermission(Claims.CanSubmitPscCloseout);
        this.canViewDataSources = userClaimService.hasPermission(Claims.CanViewSourceColumnInTasksPSC);
        this.canAccessRightShipInspectionsInActivity = userClaimService.hasPermission(Claims.CanAccessRSInspectionsInActivity);
    }

    protected onActivated(): void {
        this.enabledNewUI = this.item?.enabledNewUI;
        if (this.enabledNewUI) {
            if (this.item.closed) {
                this.loadDeficiencySeverityChanges();
            }
            if (!this.item.closed) {
                this.item.numberOfDeficiencies = this.item.deficiencies.length;
            }

            this.loadDeficiencyCategories(this.item?.deficiencies);
            this.loadOtherOpenPscs();

            this.eventAggregator.subscribe(Messages.changePscDeficiency, (deficiencies) => {
                this.loadDeficiencyCategories(deficiencies);
                this.item.numberOfDeficiencies = deficiencies.length;
                this.bindingSignaler.signal('change-deficiency');
            });
        }
    }

    loadDeficiencyCategories(deficiencies: PscDeficiencyClientModel[]) {
        this.deficiencyCategories = [
            {
                category: "Total deficiencies",
                showTab: true,
                count: deficiencies.length,
                deficiencies: deficiencies,
            },
            {
                category: "High Risk",
                showTab: true,
                count: this.getHighSeverity(deficiencies),
                deficiencies: deficiencies.filter(x => x.severityId == PscSeverityEnum.High),
                revisedSeverityCount: this.getHighSeverityChange(deficiencies),
            },
            {
                category: "Medium Risk",
                showTab: true,
                count: this.getMediumSeverity(deficiencies),
                deficiencies: deficiencies.filter(x => x.severityId == PscSeverityEnum.Medium),
                revisedSeverityCount: this.getMediumSeverityChange(deficiencies),
            },
            {
                category: "Low Risk",
                showTab: true,
                count: this.getLowSeverity(deficiencies),
                deficiencies: deficiencies.filter(x => x.severityId == PscSeverityEnum.Low),
                revisedSeverityCount: this.getLowSeverityChange(deficiencies),
            },
            {
                category: "Not Active",
                showTab: false,
                count: this.getNotActiveSeverity(deficiencies),
                deficiencies: deficiencies.filter(x => x.severityId == PscSeverityEnum.NotActive)
            }
        ];

        this.selectedCategory = this.deficiencyCategories[0];
    }

    getSeverity(severityId: PscSeverityEnum){
     
        switch(severityId){
            case PscSeverityEnum.High:
                return "high";
            case PscSeverityEnum.Medium:
                return "medium";
            case PscSeverityEnum.Low:
                return "low";
            default:
                return "not-active";
        }
    }

    @computedFrom(Reflection.getPropertyPath<PscOverview>(z => z.item),
    Reflection.getPropertyPath<PscOverview>(z => z.item.closeoutSubtaskId),
    Reflection.getPropertyPath<PscOverview>(z => z.item.pscInspectionStatusId),
    Reflection.getPropertyPath<PscOverview>(z => z.item.closeoutSubtaskStatusId))
    get isPscCloseoutDisabled(){
        //Close out in progress = If RFI has value && RFI not closed and the PSC Status not closed.
        return (this.item.closeoutSubtaskId && this.item.closeoutSubtaskStatusId != SubtaskStatusEnum.Closed) || 
        this.item.pscInspectionStatusId == PscInspectionStatusEnum.Closed;
    }

    async showContactVetSuperDialog() {
        const warning = this.item.numberOfDeficiencies == 0 && !this.item.shipDetained ? "This PSC has 0 deficiencies and 0 detentions. It will have no impact on Safety Score and thus no close out is required." : "";
        await this.rightStoreService.submitCloseOut(ProductCodes.PscCloseOutPriority, ProductCodes.PscCloseOutFree, this.item.pscInspectionId, EntityTypes.PSC, this.item.vesselId, warning, (() => {
            this.canSubmitCloseout = false;
        }).bind(this, this.canSubmitCloseout));
    }

    navigateToInspection(id) {
        window.open('insp/' + id, '_blank');
    }

    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    getHighSeverity(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.High).length;
    }

    getHighSeverityChange(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.High).length - deficiencies.filter(x => x.originalSeverityId == PscSeverityEnum.High).length;
    }

    getMediumSeverity(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.Medium).length;
    }

    getMediumSeverityChange(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.Medium).length - deficiencies.filter(x => x.originalSeverityId == PscSeverityEnum.Medium).length;
    }

    getLowSeverity(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.Low).length;
    }

    getLowSeverityChange(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.Low).length - deficiencies.filter(x => x.originalSeverityId == PscSeverityEnum.Low).length;
    }

    getNotActiveSeverity(deficiencies: PscDeficiencyClientModel[]) {
        return deficiencies.filter(x => x.severityId == PscSeverityEnum.NotActive).length;
    }
    
    @computedFrom(Reflection.getPropertyPath<PscOverview>(z => z.item),
    Reflection.getPropertyPath<PscOverview>(z => z.item.shipDetainedOverride),
    Reflection.getPropertyPath<PscOverview>(z => z.item.shipDetained))
    get shipDetained()
    {
        return this.item["shipDetainedOverride"] != null? this.item["shipDetainedOverride"]: this.item.shipDetained;
    } 

    get score() {
        if (this.getHighSeverity(this.item.deficiencies) > 0)
            return "High";
        if (this.getMediumSeverity(this.item.deficiencies) > 0)
            return "Average";

        return "Low";
    }

    @computedFrom(Reflection.getPropertyPath<PscOverview>(x => x.item), Reflection.getPropertyPath<PscOverview>(x => x.item.vesselDateOfBuild))
    public get age() {
        if (this.item) {
            if (this.item.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.item.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + " y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }


    @computedFrom("item.numberOfDaysDetained")
    get daysDetained() {
        if (this.item.numberOfDaysDetained) {
            const days = Math.floor(this.item.numberOfDaysDetained);
            const hours = Math.round((this.item.numberOfDaysDetained % 1.0) * 24);

            return `(${days} ${days == 1 ? 'day' : 'days'}${hours > 0 ? ' ' + hours : ''}${hours == 0 ? '' : (hours == 1 ? ' hour' : ' hours')})`;
        }

        return '';
    }

    activateOrDeactivateSafetyScore() {
        const isActivated = this.item.usedInSafetyScore ? false: true;
        this.pscService.activateOrDeactivateSafetyScore(this.item.pscInspectionId,isActivated)
        .then(res=>{
            this.item.hasUseInSafetyScoreDsChange=true;
            this.notificationService.show(res,'',NotificationType.Success);
        });
    }

    getPscStatus(status: PscDeficiencyStatusClientModel) { 
        const item = this.getModelForStatus(status);
        if (PscActivitiesColumns.getIsCleanAndIHSPsc(item) && this.rightShipUser) {
                return "Created";
        }
        if (PscActivitiesColumns.getIsCleanPsc(item) && !this.rightShipUser) {
                return "Clean"
        }

        return item.pscInspectionStatus;
    }

    getPscStatusClass(status: PscDeficiencyStatusClientModel) {
        const item = this.getModelForStatus(status);
        if (((PscActivitiesColumns.getIsCleanAndIHSPsc(item) && this.rightShipUser) || (PscActivitiesColumns.getIsCleanPsc(item) && !this.rightShipUser))) {
                return "circle clean";
        }
        if (item.pscInspectionStatus){
            return `circle ${item.pscInspectionStatus.split(' ').join('-').toLowerCase()}`;
        }
    }

    @computedFrom("item.pscInspectionStatusId", "item.hasUseInSafetyScoreDsChange", "item.canActivateOrDeactivateSafetyScore", "item.usedInSafetyScore",
        "item.hasUseInDetentionSafetyScoreDsChange")
    get canShowSafetyScoreButton(){
        return this.item.pscInspectionStatusId != PscInspectionStatusEnum.Misreported &&
            !this.item.hasUseInSafetyScoreDsChange &&
            !this.item.hasUseInDetentionSafetyScoreDsChange &&
            this.item.canActivateOrDeactivateSafetyScore &&
            this.userClaimService.hasPermission(Claims.CanActivatePSCinSafetyScore) &&
            !this.item.usedInSafetyScore;

    }

    @computedFrom("item", "item.hasDataSourceConflicts", "item.pscInspectionStatusId")
    get showConflictBanner(): boolean
    {
        return this.item && this.item.hasDataSourceConflicts 
            && this.item.pscInspectionStatusId != PscInspectionStatusEnum.Misreported
            && this.item.pscInspectionStatusId != PscInspectionStatusEnum.Closed;
    }

    @computedFrom("item", "item.pscInspectionStatusId", "item.usedInSafetyScore", "item.hasUseInSafetyScoreDsChange")
    get showActiveSafetyScoreRemider()
    {
        return this.item && this.item.pscInspectionStatusId != PscInspectionStatusEnum.Closed && !this.item.usedInSafetyScore && this.getConcatenatedDataSources() == 'RSDS';
    }

    editNumberOfDeficiencies()
    {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.EditNumberOfDeficienciesDialog, {
            pscId: this.item.pscInspectionId
        }, true, false, false, "right")
    }

    @computedFrom("canEditPSCDetailsPermission", "item.isInconsistentState")
    get canEditPSCDetails()
    {
        return this.canEditPSCDetailsPermission && !this.item.isInconsistentState;
    }

    @computedFrom("canEditPSCDetailsPermission", "item.isInconsistentState", "canViewPSCEditsPermission")
    get canViewOrEditPSCDetails()
    {
        return (this.canEditPSCDetailsPermission && !this.item.isInconsistentState) || this.canViewPSCEditsPermission;
    }

    @computedFrom("item", "item.pscInspectionStatusId")
    get isCleanPsc()
    {
        const status = this.item && this.item.statuses? Enumerable.from(this.item.statuses).orderBy(x => x.date).firstOrDefault(): null;
        const item = this.getModelForStatus(status);
        return ((PscActivitiesColumns.getIsCleanAndIHSPsc(item) && this.rightShipUser) || (PscActivitiesColumns.getIsCleanPsc(item) && !this.rightShipUser));
    }

    getModelForStatus(status?: PscDeficiencyStatusClientModel)
    {
        const item = { entityTypeId: EntityTypes.PSC, numberOfDeficiencies: this.item.numberOfDeficiencies, shipDetained: this.item.shipDetained,
            concatenatedDataSources: this.getConcatenatedDataSources(), pscInspectionStatus: status? status.name: "",
            statusId: status? status.statusId: this.item.pscInspectionStatusId, pscInspectionStatusId: null 
        };
        return item;
    }

    getConcatenatedDataSources()
    {
        return this.item.concatenatedDataSources;
    }
    @computedFrom("item", "item.numberOfDeficienciesOverride", "item.shipDetainedOverride", "item.numberOfDeficiencies", "item.shipDetained", "item.statuses.length")
    get showCloseOutAction()
    {
        if(!this.item) return false;

        const lastStatus = this.item && this.item.statuses? Enumerable.from(this.item.statuses).orderByDescending(s => s.date).firstOrDefault(): null;
        if(!lastStatus) return false;

        const hideClosoutAction = moment(lastStatus.date).add(5, 'year').isSameOrBefore(moment())
        || this.item.followUpInspection
        || (
                !(this.item.numberOfDeficienciesOverride? this.item.numberOfDeficienciesOverride: this.item.numberOfDeficiencies)
                && !(this.item.shipDetainedOverride? this.item.shipDetainedOverride: this.item.shipDetained)
            );

        return !hideClosoutAction;
    }

    @computedFrom("item", "item.country")
    get country(){
        return this.item && this.item.country? `(${this.item.country})` : "";
    }

    loadDeficiencySeverityChanges() {
        this.item.deficiencies.forEach(deficiency => {
            if (deficiency.severity && deficiency.originalSeverity != deficiency.severity) {
                const originalSeverity = deficiency.originalSeverity ?? 'No Score';
                if (this.severityChanges.findIndex(x => x.originalSeverity == originalSeverity && x.revisedSeverity == deficiency.severity) > 0) {
                    const change = this.severityChanges.find(x => x.originalSeverity == originalSeverity && x.revisedSeverity == deficiency.severity);
                    change.count++;
                } else {
                    const change: DeficiencySeverityChange = {
                        originalSeverity: originalSeverity,
                        revisedSeverity: deficiency.severity,
                        count: 1
                    }
                    this.severityChanges.push(change);
                }
            }
        });
    }

    navigateToVesselSubscore() {
        window.open(`${this.configService.config.urls.rightShipPlatformUrl}/vessel/${this.item.vesselId}/safetyscore/?tab=vessel-subscores`, "_blank");
    }

    selectCategory(category) {
        this.selectedCategory = category;
    }

    async loadOtherOpenPscs() {
        const myVessels = await this.myVesselService.getMyVessels();
        //Get vessels lookup for the filter
		this.vessels = myVessels?.result.map((vessel) => {
			return {
				id: vessel.vesselId,
				name: vessel.vesselName,
				imo: vessel.identifier
			};
		});

        const request: GetTasksRequest = {
            vessels: this.vessels?.map((vessel: LookupValue) => vessel.id),
            statuses: [],
            rowPerPage: 50,
            priority: TaskPriorityEnum.Standard, //Standard
            types: [EntityTypes.PSC],
            page: 1,
            orderBy: "ASC",
            sortBy: "Date"
        };

        //Get pending tasks
        if (request.vessels?.length > 0) {

            //get standard pending tasks with paging
            request.prioritized = false;
            const response = await this.myTaskService.getPendingTask(request);
            if (response?.result != null) {
                this.pendingTasks = response.result;
            }
        }
    }

    getDateString(date: Date): string {
        return moment(new Date(date)).format("DD MMM YYYY");
    }

    getTaskDescription(description: string): string {
        // remove the "(High:..., Med:..., Low:...)" from the description
        if (!this.enabledNewUI && description && description.indexOf("(High:") > 0)
            return description.slice(0, description.indexOf("(High:")-1) + description.slice(description.indexOf(" Det:")-1, description.length);
        else
            return description;
    }

    async taskSubmitCloseOut(task: TaskModel) {
        await this.rightStoreService.submitCloseOut(ProductCodes.PscCloseOutPriority, ProductCodes.PscCloseOutFree, task.taskEntityId, EntityTypes.PSC, task.vesselId, '', (() => {
            this.canSubmitCloseout = false;
        }).bind(this, this.canSubmitCloseout));
    }
}
